"use client";

import { ReactNode } from "react";

import { Anchor, AnchorProps, Button, ButtonProps } from "@mantine/core";

import { useProtectedEmail } from "@/app/lib/hooks/useProtectedEmail";

interface ProtectedEmailProps {
  /** A `CONTACT_EMAIL_BASE64` entry. */
  address: string;
  /** The link's text. Defaults to the address, which then shows once the page has loaded. */
  children?: ReactNode;
}

/**
 * A mailto link for a company address that never ships whole in the page or its bundle
 * (`useProtectedEmail`). Server components render it with a `CONTACT_EMAIL_BASE64` entry. Until
 * the page loads, and without the script, the link has no href, and no text unless given some.
 */
export function ProtectedEmailLink({
  address,
  children,
  ...props
}: ProtectedEmailProps & AnchorProps) {
  const email = useProtectedEmail(address);

  return (
    <Anchor href={email ? `mailto:${email}` : undefined} {...props}>
      {children ?? email}
    </Anchor>
  );
}

/** `ProtectedEmailLink` as a button. */
export function ProtectedEmailButton({
  address,
  children,
  ...props
}: ProtectedEmailProps & ButtonProps) {
  const email = useProtectedEmail(address);

  return (
    <Button component="a" href={email ? `mailto:${email}` : undefined} {...props}>
      {children ?? email}
    </Button>
  );
}
